// directives/v-hammer.ts
import type { Directive } from "vue";
import Hammer from "hammerjs";

interface HammerOptions {
  event?: string | string[]; // 支持空格或数组：'panstart panright panend'
  handler?: (e: any) => void;
  direction?: 'horizontal' | 'vertical' | 'all';
  threshold?: number; // 最小滑动距离
  velocity?: number; // 最小滑动速度
  edgeStart?: number; // 左侧边缘触发宽度(px)
}

export const vHammer: Directive<HTMLElement, HammerOptions> = {
  mounted(el, binding) {
    const hammer = new Hammer(el);

    // 默认监听事件
    const eventInput = binding.value?.event ?? "swipe";
    const handler = binding.value?.handler || ((e: any) => console.log("Gesture:", e.type));

    // 默认 touch-action，允许水平滑动
    if (!el.style.touchAction) {
      el.style.touchAction = 'pan-y';
    }

    // 配置方向和阈值
    const direction = binding.value?.direction || 'horizontal';
    const threshold = binding.value?.threshold ?? 10;
    const velocity = binding.value?.velocity ?? 0.3;
    const edgeStart = binding.value?.edgeStart ?? 24;

    const swipe = hammer.get('swipe');
    if (swipe) {
      let dir: number = (Hammer as any).DIRECTION_HORIZONTAL as number;
      if (direction === 'vertical') dir = (Hammer as any).DIRECTION_VERTICAL as number;
      if (direction === 'all') dir = (Hammer as any).DIRECTION_ALL as number;
      swipe.set({ direction: dir as any, threshold, velocity } as any);
    }

    // 配置 pan 以支持 Android 内嵌环境的边缘滑动返回
    const pan = hammer.get('pan') || new (Hammer as any).Pan();
    hammer.add(pan);
    if (pan) {
      let dir: number = (Hammer as any).DIRECTION_HORIZONTAL as number;
      if (direction === 'vertical') dir = (Hammer as any).DIRECTION_VERTICAL as number;
      if (direction === 'all') dir = (Hammer as any).DIRECTION_ALL as number;
      pan.set({ direction: dir as any, threshold: Math.max(1, threshold) } as any);
    }

    // 归一化事件数组，并在仅写 'pan' 时展开常见子事件
    let events: string[] = Array.isArray(eventInput)
      ? eventInput
      : String(eventInput).trim().split(/\s+/);
    if (events.length === 1 && events[0] === 'pan') {
      events = ['panstart', 'panright', 'panend'];
    }

    // 把配置也存起来，便于 updated 使用
    (el as any).__hammer_edge__ = edgeStart;

    events.forEach((ev) => hammer.on(ev, handler));

    // 保存实例，卸载时用
    (el as any).__hammer__ = hammer;
    (el as any).__hammer_events__ = events;
    (el as any).__hammer_handler__ = handler;
  },
  updated(el, binding) {
    const hammer = (el as any).__hammer__;
    if (!hammer) return;

    const prevEvents: string[] = (el as any).__hammer_events__ || [];
    const prevHandler = (el as any).__hammer_handler__;

    const eventInput = binding.value?.event ?? "swipe";
    const nextHandler = binding.value?.handler || ((e: any) => console.log("Gesture:", e.type));
    let nextEvents: string[] = Array.isArray(eventInput)
      ? eventInput
      : String(eventInput).trim().split(/\s+/);
    if (nextEvents.length === 1 && nextEvents[0] === 'pan') {
      nextEvents = ['panstart', 'panright', 'panend'];
    }

    // 可能更新 swipe 配置
    const direction = binding.value?.direction || 'horizontal';
    const threshold = binding.value?.threshold ?? 10;
    const velocity = binding.value?.velocity ?? 0.3;
    const edgeStart = binding.value?.edgeStart ?? (el as any).__hammer_edge__ ?? 24;
    const swipe = hammer.get('swipe');
    if (swipe) {
      let dir: number = (Hammer as any).DIRECTION_HORIZONTAL as number;
      if (direction === 'vertical') dir = (Hammer as any).DIRECTION_VERTICAL as number;
      if (direction === 'all') dir = (Hammer as any).DIRECTION_ALL as number;
      swipe.set({ direction: dir as any, threshold, velocity } as any);
    }
    const pan = hammer.get('pan');
    if (pan) {
      let dir: number = (Hammer as any).DIRECTION_HORIZONTAL as number;
      if (direction === 'vertical') dir = (Hammer as any).DIRECTION_VERTICAL as number;
      if (direction === 'all') dir = (Hammer as any).DIRECTION_ALL as number;
      pan.set({ direction: dir as any, threshold: Math.max(1, threshold) } as any);
    }
    (el as any).__hammer_edge__ = edgeStart;

    // 如果事件集合或处理函数发生变化，则更新绑定
    const sameEvents = prevEvents.length === nextEvents.length && prevEvents.every((e, i) => e === nextEvents[i]);
    if (!sameEvents || prevHandler !== nextHandler) {
      // 解绑旧
      prevEvents.forEach((ev) => prevHandler && hammer.off(ev, prevHandler));
      // 绑定新
      nextEvents.forEach((ev) => hammer.on(ev, nextHandler));
      (el as any).__hammer_events__ = nextEvents;
      (el as any).__hammer_handler__ = nextHandler;
    }
  },
  unmounted(el) {
    const hammer = (el as any).__hammer__;
    const events: string[] = (el as any).__hammer_events__ || [];
    const handler = (el as any).__hammer_handler__;
    if (hammer) {
      if (events && handler) {
        events.forEach((ev) => hammer.off(ev, handler));
      }
      hammer.destroy();
    }
  },
};
